import { Injectable, Inject } from '@angular/core';
import { Observable, of, BehaviorSubject } from 'rxjs';
import { map, debounceTime, distinctUntilChanged, switchMap, catchError, tap, finalize } from 'rxjs/operators';
import { CacheService } from './cache.service';
import { LoggerService } from '../shared/logger.service';

export interface RoutinesSearchConfig {
  debounceTime: number;
  minSearchLength: number;
  cacheTimeout: number;
  maxResults: number;
}

export interface RoutinesSearchResult {
  data: any[];
  totalCount: number;
  searchTime: number;
  fromCache: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class RoutinesOptimizationService {
  private readonly defaultConfig: RoutinesSearchConfig = {
    debounceTime: 300,
    minSearchLength: 2,
    cacheTimeout: 5 * 60 * 1000, // 5 minutos
    maxResults: 1000
  };

  private searchSubject = new BehaviorSubject<string>('');
  private loadingSubject = new BehaviorSubject<boolean>(false);

  constructor(
    private cacheService: CacheService,
    @Inject(LoggerService) private logger: LoggerService
  ) {}

  /**
   * Configura busca otimizada com debounce e cache
   */
  setupOptimizedSearch(
    searchFunction: (query: string) => Observable<any[]>,
    config?: Partial<RoutinesSearchConfig>
  ): Observable<RoutinesSearchResult> {
    const searchConfig = { ...this.defaultConfig, ...config };

    return this.searchSubject.pipe(
      debounceTime(searchConfig.debounceTime),
      distinctUntilChanged(),
      switchMap(query => {
        if (query.length < searchConfig.minSearchLength) {
          this.loadingSubject.next(false);
          return of({ data: [], totalCount: 0, searchTime: 0, fromCache: false });
        }

        this.loadingSubject.next(true);
        const startTime = performance.now();
        
        return this.performOptimizedSearch(query, searchFunction, searchConfig).pipe(
          map(result => ({
            ...result,
            searchTime: performance.now() - startTime
          })),
          catchError(error => {
            this.logger.error('Error in optimized search:', error);
            this.loadingSubject.next(false);
            return of({ data: [], totalCount: 0, searchTime: 0, fromCache: false });
          })
        );
      })
    );
  }

  /**
   * Executa busca otimizada com cache
   */
  private performOptimizedSearch(
    query: string,
    searchFunction: (query: string) => Observable<any[]>,
    config: RoutinesSearchConfig
  ): Observable<RoutinesSearchResult> {
    const cacheKey = `routines_search_${query.toLowerCase()}`;

    return this.cacheService.get<RoutinesSearchResult>(
      cacheKey,
      () =>
        searchFunction(query).pipe(
          map(results => {
            const limitedResults = results.slice(0, config.maxResults);
            const result: RoutinesSearchResult = {
              data: limitedResults,
              totalCount: results.length,
              searchTime: 0,
              fromCache: false
            };
            this.logger.debug('Search results computed for query:', query);
            return result;
          })
        ),
      { ttl: config.cacheTimeout }
    ).pipe(
      tap(() => this.loadingSubject.next(false))
    );
  }

  /**
   * Executa busca com filtros otimizados
   */
  performClientSideFilter(
    data: any[],
    query: string,
    filters?: { [key: string]: any }
  ): any[] {
    if (!query && !filters) {
      return data;
    }

    const startTime = performance.now();
    const lowerQuery = query.toLowerCase();

    const filteredData = data.filter(item => {
      // Filtro de texto
      if (query) {
        const matchesText = Object.values(item).some(value => 
          value && value.toString().toLowerCase().includes(lowerQuery)
        );
        if (!matchesText) return false;
      }

      // Filtros adicionais
      if (filters) {
        for (const [key, filterValue] of Object.entries(filters)) {
          if (filterValue && item[key] !== filterValue) {
            return false;
          }
        }
      }

      return true;
    });

    const searchTime = performance.now() - startTime;
    this.logger.debug(`Client-side filter completed in ${searchTime.toFixed(2)}ms`);

    return filteredData;
  }

  /**
   * Otimiza dados para busca rápida
   */
  optimizeDataForSearch(data: any[]): any[] {
    return data.map(item => ({
      ...item,
      // Criar índices de busca para campos comuns
      _searchIndex: Object.values(item)
        .filter(value => value && typeof value === 'string')
        .join(' ')
        .toLowerCase()
    }));
  }

  /**
   * Busca rápida usando índice
   */
  fastSearch(data: any[], query: string): any[] {
    const lowerQuery = query.toLowerCase();
    return data.filter(item => 
      item._searchIndex && item._searchIndex.includes(lowerQuery)
    );
  }

  /**
   * Emite novo termo de busca
   */
  search(query: string): void {
    this.searchSubject.next(query);
  }

  /**
   * Observable de loading
   */
  get loading$(): Observable<boolean> {
    return this.loadingSubject.asObservable();
  }

  /**
   * Limpa cache de busca
   */
  clearSearchCache(): void {
    // Sem suporte a clear by pattern no CacheService atual; limpar tudo
    this.cacheService.clear();
    this.logger.debug('Search cache cleared');
  }

  /**
   * Obtém estatísticas de cache
   */
  getCacheStats(): Observable<any> {
    return this.cacheService.getStats();
  }

  /**
   * Configura cache para rotinas específicas
   */
  cacheRoutines(routines: any[], personId: string, date: string): void {
    const cacheKey = `routines_${personId}_${date}`;
    this.cacheService
      .get<any[]>(cacheKey, () => of(routines), { ttl: this.defaultConfig.cacheTimeout })
      .subscribe(() => this.logger.debug(`Routines cached for person ${personId} on ${date}`));
  }

  /**
   * Recupera rotinas do cache
   */
  getCachedRoutines(personId: string, date: string): Observable<any[]> {
    const cacheKey = `routines_${personId}_${date}`;
    // Retornará do cache se existir, senão completa sem emitir até alguém cachear
    return this.cacheService.get<any[]>(cacheKey, () => of([]));
  }
}
